import { Controller } from '@hotwired/stimulus'
import { connectUserData, getElementContext } from '../helpers/controllerHelpers/utils'
import { passesConditions } from '../api/helpers/conditions'
import type {
  Condition,
  UserDataValue,
  Component,
  FlowPage,
  ConditionalTag,
} from '../api/models/types'

export default class extends Controller {
  static values = {
    isPage: Boolean,
    componentOrPage: Object,
    pageId: String,
  }
  isPage: boolean
  componentOrPage: Component | FlowPage | undefined | null
  conditional_tags: ConditionalTag[] | undefined
  excludeChildren: boolean

  declare userDataValue: UserDataValue
  disconnectUserData: (() => void) | undefined

  connect() {
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)

    const { isPage, component, page } = getElementContext(this) ?? {}
    this.isPage = isPage
    this.componentOrPage = isPage ? page : component

    if (!isPage)
      this.excludeChildren = (this.componentOrPage as Component).type === 'Container' ? true : false
    this.conditional_tags = this.componentOrPage?.conditional_tags
    this.conditionallyApplyStyles()
  }

  disconnect() {
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
  }

  pageIdValueChanged() {
    if (!this.isPage) return

    const { page } = getElementContext(this) ?? {}
    this.componentOrPage = page
    this.conditional_tags = page?.conditional_tags

    this.conditionallyApplyStyles()
  }

  conditionallyApplyStyles() {
    const key = this.componentOrPage?.key
    if (!key || !this.element || !this.userDataValue || !this.conditional_tags) return

    const conditional_tags = this.conditional_tags
    const userDataValue = this.userDataValue
    const excludeChildren = this.excludeChildren

    const applyClassesToElementAndChildren = (el: any, classes: string[]) => {
      if (
        el.classList.contains('Flow-Page') ||
        el.classList.contains('Flow-Component') ||
        el.classList.contains('Flow-Element')
      )
        el.classList.add(...classes)
      if (!excludeChildren) {
        for (const child of el.children) {
          applyClassesToElementAndChildren(child, classes)
        }
      }
    }

    const removeClassesFromElementAndChildren = (el: any, classes: string[]) => {
      el.classList.remove(...classes)
      if (!excludeChildren) {
        for (const child of el.children) {
          removeClassesFromElementAndChildren(child, classes)
        }
      }
    }

    const tags = conditional_tags || []
    tags.forEach((t: { tag: string; conditions: Condition[] }) => {
      const didPassConditions = passesConditions(
        t.conditions,
        userDataValue as unknown as Record<string, unknown>
      )
      const tagToApply = this.isPage ? `PageTag-${t.tag}` : `ComponentTag-${t.tag}`
      if (didPassConditions) {
        setTimeout(() => {
          applyClassesToElementAndChildren(this.element, [tagToApply])
        }, 0)
      } else {
        setTimeout(() => {
          removeClassesFromElementAndChildren(this.element, [tagToApply])
        }, 0)
      }
    })
  }

  userDataValueChanged() {
    this.conditionallyApplyStyles()
  }

  jsonValueChanged() {
    const { page, component } = getElementContext(this) ?? {}
    this.componentOrPage = this.isPage ? page : component
    if (!this.isPage)
      this.excludeChildren = (this.componentOrPage as Component).type === 'Container' ? true : false
    this.conditional_tags = this.componentOrPage?.conditional_tags

    this.conditionallyApplyStyles()
  }
}
